import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "..";
import {
  BaseReceiptRequestBody,
  CartRequestBody,
  ReceiptRequestBody,
  ReceiptType,
} from "@/types/receipt";
import { isTakeOut } from "@/stores/cash-register";
import i18next from "i18next";
import usePickupStore from "@/stores/pickup";
import { isInfoDiscount } from "@/helpers/discount";

const selectSelf = (state: RootState) => state.cart;

export const selectCartProducts = createSelector(selectSelf, (state) => state.products);
export const selectCartDiscounts = createSelector(selectSelf, (state) => state.discounts);
export const selectAgeCheck = createSelector(selectSelf, (state) => state.ageCheck);

export const selectCartProductsTotal = createSelector(selectCartProducts, (products) =>
  products.reduce((acc, item) => {
    return acc + item.priceCents * item.quantity;
  }, 0)
);
export const selectCartDiscountTotal = createSelector(selectCartDiscounts, (vouchers) =>
  vouchers.reduce((acc, voucher) => {
    if (isInfoDiscount(voucher)) return acc;
    return acc + voucher.priceCents * voucher.quantity;
  }, 0)
);
export const selectCartTotal = createSelector(
  [selectCartProductsTotal, selectCartDiscountTotal],
  (productTotal, discountTotal) => productTotal + discountTotal
);

export const selectLegalAge = createSelector(selectSelf, (cart) => cart.currentLegalAge);
export const selectRequiredAge = createSelector(selectSelf, (cart) => cart.ageCheck.requiredAge);

export const selectCartRequestBody = createSelector(
  [
    (state: RootState) => state.cart.products,
    (state: RootState) => state.cart.discounts,
    (state: RootState) => state.cart.ageCheck,
    (state: RootState) => state.cart.currentLegalAge,
    (state: RootState) => state.cart.legalAgeQr,
    (_: RootState, params?: { qrCode?: string; acceptedAge?: number; resetAgeCheck?: boolean }) =>
      params,
  ],
  (products, discounts, ageCheck, legalAge, legalAgeQr, params = {}): CartRequestBody => {
    const { qrCode, acceptedAge, resetAgeCheck } = params;

    return {
      lineItems: {
        products,
        discounts,
      },
      qrCode: qrCode ?? null,
      legalAge: acceptedAge ?? legalAge,
      legalAgeQr,
      ageCheck: resetAgeCheck ? { requiredAge: null, lineItems: [] } : ageCheck,
    };
  }
);

export const selectReceiptRequestBody = createSelector(
  [
    (state: RootState) => state.cart.products,
    (state: RootState) => state.cart.discounts,
    (state: RootState) => state.cart.legalAgeQr,
    (state: RootState) => state.cart.currentLegalAge,
  ],
  // A lot of data doesn't comes from other sources then Redux store right now. This isn't following conventions...
  // but the data will be added to Redux eventually, that's why this setup with a selector is used.
  (products, discounts, qrCode, legalAge): ReceiptRequestBody | undefined => {
    const baseReceipt: BaseReceiptRequestBody = {
      receipt: {
        kioskLanguage: i18next.language,
        lineItems: {
          products,
          discounts,
        },
        ...(qrCode ? { legalAgeQr: qrCode } : {}),
        ...(legalAge ? { legalAge } : {}),
      },
    };
    if (isTakeOut()) return { receipt: { ...baseReceipt.receipt, type: ReceiptType.TakeOut } };
    const customer = usePickupStore.getState().getCustomer();
    const pickupStartAt = usePickupStore.getState().pickupStartAt;

    if (!customer || !pickupStartAt) return undefined;
    return {
      receipt: { ...baseReceipt.receipt, type: ReceiptType.Pickup, customer, pickupStartAt },
    };
  }
);
